// Animation presets V1 (CREATIVE-MODEL-V1.md §7.2). The editor and AI expand
// a preset into explicit keyframes ONCE, when applied; the track keeps
// `preset` only as provenance. Runtime and compiler never resolve presets,
// so changing a preset definition never alters an existing version.

import type { AnimationTrack, Easing } from "./schema/document.js";

interface PresetDefinition {
  readonly phase: AnimationTrack["phase"];
  readonly easing: Easing;
  readonly iterations?: number | "infinite";
  readonly direction?: AnimationTrack["direction"];
  readonly keyframes: (distance: number) => AnimationTrack["keyframes"];
}

export const ANIMATION_PRESETS_V1 = {
  fadeIn: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: () => [
      { at: 0, props: { opacity: 0 } },
      { at: 1, props: { opacity: 1 } },
    ],
  },
  fadeOut: {
    phase: "exit",
    easing: "easeIn",
    keyframes: () => [
      { at: 0, props: { opacity: 1 } },
      { at: 1, props: { opacity: 0 } },
    ],
  },
  slideUp: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: (d) => [
      { at: 0, props: { opacity: 0, offsetY: d } },
      { at: 1, props: { opacity: 1, offsetY: 0 } },
    ],
  },
  slideLeft: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: (d) => [
      { at: 0, props: { opacity: 0, offsetX: d } },
      { at: 1, props: { opacity: 1, offsetX: 0 } },
    ],
  },
  zoomIn: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: () => [
      { at: 0, props: { opacity: 0, scaleX: 0.8, scaleY: 0.8 } },
      { at: 1, props: { opacity: 1, scaleX: 1, scaleY: 1 } },
    ],
  },
  // 7.6D (additive; existing definitions above never change): the four slide
  // directions, zoom out and exits. Names say where the element MOVES TO.
  slideDown: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: (d) => [
      { at: 0, props: { opacity: 0, offsetY: -d } },
      { at: 1, props: { opacity: 1, offsetY: 0 } },
    ],
  },
  slideRight: {
    phase: "entrance",
    easing: "easeOut",
    keyframes: (d) => [
      { at: 0, props: { opacity: 0, offsetX: -d } },
      { at: 1, props: { opacity: 1, offsetX: 0 } },
    ],
  },
  zoomOut: {
    phase: "exit",
    easing: "easeIn",
    keyframes: () => [
      { at: 0, props: { opacity: 1, scaleX: 1, scaleY: 1 } },
      { at: 1, props: { opacity: 0, scaleX: 0.8, scaleY: 0.8 } },
    ],
  },
  slideOutUp: {
    phase: "exit",
    easing: "easeIn",
    keyframes: (d) => [
      { at: 0, props: { opacity: 1, offsetY: 0 } },
      { at: 1, props: { opacity: 0, offsetY: -d } },
    ],
  },
  slideOutDown: {
    phase: "exit",
    easing: "easeIn",
    keyframes: (d) => [
      { at: 0, props: { opacity: 1, offsetY: 0 } },
      { at: 1, props: { opacity: 0, offsetY: d } },
    ],
  },
  slideOutLeft: {
    phase: "exit",
    easing: "easeIn",
    keyframes: (d) => [
      { at: 0, props: { opacity: 1, offsetX: 0 } },
      { at: 1, props: { opacity: 0, offsetX: -d } },
    ],
  },
  slideOutRight: {
    phase: "exit",
    easing: "easeIn",
    keyframes: (d) => [
      { at: 0, props: { opacity: 1, offsetX: 0 } },
      { at: 1, props: { opacity: 0, offsetX: d } },
    ],
  },
  pulse: {
    phase: "emphasis",
    easing: "easeInOut",
    direction: "alternate",
    keyframes: () => [
      { at: 0, props: { scaleX: 1, scaleY: 1 } },
      { at: 1, props: { scaleX: 1.05, scaleY: 1.05 } },
    ],
  },
  // 7.6F (additive): gameplay feedback, typically started by an interaction (manual tracks).
  shake: {
    phase: "emphasis",
    easing: "linear",
    keyframes: (d) => [
      { at: 0, props: { offsetX: 0 } },
      { at: 0.2, props: { offsetX: -d } },
      { at: 0.4, props: { offsetX: d } },
      { at: 0.6, props: { offsetX: -d } },
      { at: 0.8, props: { offsetX: d } },
      { at: 1, props: { offsetX: 0 } },
    ],
  },
  pop: {
    phase: "emphasis",
    easing: "easeOut",
    keyframes: () => [
      { at: 0, props: { scaleX: 1, scaleY: 1 } },
      { at: 0.5, props: { scaleX: 1.25, scaleY: 1.25 } },
      { at: 1, props: { scaleX: 1, scaleY: 1 } },
    ],
  },
} as const satisfies Readonly<Record<string, PresetDefinition>>;

export type AnimationPresetId = keyof typeof ANIMATION_PRESETS_V1;
export const ANIMATION_PRESET_VERSION = 1;

export interface PresetRequest {
  readonly preset: AnimationPresetId;
  readonly trackId: string;
  readonly at: number;
  readonly duration: number;
  readonly iterations?: number | "infinite";
  /** Travel distance in px for slide presets (default 16). */
  readonly distance?: number;
}

/** Deterministic expansion: same request ⇒ same track. */
export function expandPreset(req: PresetRequest): AnimationTrack {
  const def: PresetDefinition = ANIMATION_PRESETS_V1[req.preset];
  const distance = req.distance ?? 16;
  const iterations = req.iterations ?? def.iterations;
  return {
    id: req.trackId,
    phase: def.phase,
    start: { mode: "timeline", at: req.at },
    duration: req.duration,
    ...(iterations !== undefined ? { iterations } : {}),
    ...(def.direction !== undefined ? { direction: def.direction } : {}),
    easing: def.easing,
    keyframes: def.keyframes(distance),
    preset: {
      id: req.preset,
      version: ANIMATION_PRESET_VERSION,
      ...(req.distance !== undefined ? { params: { distance: req.distance } } : {}),
    },
  };
}
